import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCart } from '../../context/CartContext';
import Button from './Button';
import Card from './Card';
import ProductImageCarousel from './ProductImageCarousel';
import QuantitySelector from './QuantitySelector';
import { publicApi } from '../../services/api';
import { prefetchProductDetail, primeProductDetailCache } from '../../services/publicCatalogCache';
import { trackCartEvent, trackSilentFailure } from '../../services/analytics';

const ProductCard = ({
  product,
  showAdd = false,
  imageLoading = 'lazy',
  imageFetchPriority = 'auto',
}) => {
  // Normalise product id for compatibility with backend (Prisma) and placeholder data
  const productId = product._id ?? product.id;
  const normalisedProduct = product._id ? product : { ...product, _id: productId };
  const { dispatch } = useCart();
  const [qty, setQty] = useState(1);
  const available = Number(product.inventory ?? product.stock ?? Infinity);
  const isOut = !Number.isFinite(available) ? false : available <= 0;
  const maxQty = Number.isFinite(available) ? Math.max(0, available) : undefined;
  const productImages = Array.isArray(product.images) && product.images.length > 0
    ? product.images
    : [product.imageUrl || product.image_url || product.image].filter(Boolean);
  const productRouteState = { productPreview: normalisedProduct };

  const prefetchDetail = () => {
    primeProductDetailCache(normalisedProduct);
    void prefetchProductDetail(productId, async () => {
      const response = await publicApi.get(`/products/${productId}`);
      return response.data;
    });
  };
  // Loading state omitted in this PR, but button handles disabled well.

  const addToCart = (e) => {
    e.preventDefault();
    trackCartEvent({
      action: 'add_to_cart',
      status: 'started',
      productId: normalisedProduct._id || normalisedProduct.id,
      metadata: {
        product_name: normalisedProduct.name,
        quantity: qty,
      },
    });

    try {
      dispatch({ type: 'ADD', payload: { product: normalisedProduct, qty } });
      setQty(1);
      
      trackCartEvent({
        action: 'add_to_cart',
        status: 'succeeded',
        productId: normalisedProduct._id || normalisedProduct.id,
        cartValue: normalisedProduct.price * qty,
        metadata: {
          product_name: normalisedProduct.name,
          quantity: qty,
        },
      });
      // Requirement 3.4 will likely dispatch a toast here through a context higher up later.
    } catch (err) {
      trackCartEvent({
        action: 'add_to_cart',
        status: 'failed',
        productId: normalisedProduct._id || normalisedProduct.id,
        errorMessage: err.message,
      });
      
      trackSilentFailure({
        context: 'add_to_cart_card',
        expectedState: 'item_added',
        actualState: 'exception_thrown',
        metadata: { error: err.message },
      });
    }
  };

  return (
    <Card 
      variant="elevated" 
      hoverable 
      className="flex flex-col h-full bg-white dark:bg-brand-surface group transition-all duration-300 hover:-translate-y-[2px] md:hover:rotate-2 md:hover:scale-[1.02] origin-center shadow-product-normal hover:shadow-product-hover rounded-[16px] md:rounded-card p-[6px] md:p-[8px]"
    >
      <Link 
        to={`/product/${productId}`}
        state={productRouteState}
        className="block relative w-full aspect-[4/5] md:aspect-square overflow-hidden rounded-[12px] md:rounded-[14px] bg-light-secondary dark:bg-brand-elevated"
        onMouseEnter={prefetchDetail}
        onFocus={prefetchDetail}
        onTouchStart={prefetchDetail}
      >
        <ProductImageCarousel
          images={productImages}
          alt={product.name}
          imageClassName="w-full h-full object-cover transition-transform duration-slow group-hover:scale-105"
          showArrows={false}
          showDots={productImages.length > 1}
          loading={imageLoading}
          fetchPriority={imageFetchPriority}
          autoPlay={productImages.length > 1}
          autoPlayInterval={4500}
        />
      </Link>
      
      <div className="flex flex-col flex-grow p-2 md:p-3 pt-2 md:pt-3">
        <Link
          to={`/product/${productId}`}
          state={productRouteState}
          onMouseEnter={prefetchDetail}
          onFocus={prefetchDetail}
          onTouchStart={prefetchDetail}
        >
          <h3 
            className="font-medium text-[13px] md:text-[16px] leading-[1.3] md:leading-[1.4] text-light-text-primary dark:text-brand-text-primary line-clamp-2 mb-[2px] md:mb-[4px] hover:text-light-text-secondary dark:hover:text-brand-text-secondary transition-colors"
            title={product.name}
          >
            {product.name}
          </h3>
        </Link>
        <p className="font-bold text-[14px] md:text-[18px] text-light-text-primary dark:text-brand-text-primary mb-[8px] md:mb-[12px]">
          UGX {product.price.toLocaleString()}
        </p>

        {showAdd && (
          <div className="mt-auto flex flex-col gap-[6px] md:gap-[12px]">
            {/* Stock indicator logic: Show if stock <= 3 */}
            {Number.isFinite(available) && available <= 3 && available > 0 && (
              <div className="self-start">
                <span className="inline-block px-[8px] md:px-[12px] py-[2px] md:py-[4px] text-[10px] md:text-[12px] font-medium bg-[#FEE2E2] text-[#DC2626] rounded-pill">
                  Only {available} left
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-center">
              <QuantitySelector 
                quantity={qty} 
                onQuantityChange={setQty} 
                max={maxQty}
                className="self-center"
              />
            </div>

            <Button
              className="w-full h-[34px] md:h-[44px] text-[12px] md:text-[14px] rounded-[8px] md:rounded-[10px] bg-black text-white font-medium hover:bg-black/90 active:scale-[0.98] transition-all flex items-center justify-center whitespace-nowrap px-1 md:px-4 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isOut}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                addToCart(e);
              }}
            >
              {isOut ? 'Out of Stock' : 'Add to Cart'}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
};

export default memo(ProductCard);
